// The scatter is not drawn here at all — it is the platform's own ScatterChart, the same
// component the chart library ships for the dashboards. This file is only the adapter that maps a
// normalized chat chart onto its props, exactly as ChatDoughnut does for DonutPieChart in
// RadialChart.tsx. Keeping the drawing in one place is what stops the chat panel and the screens
// from slowly disagreeing about what a scatter looks like.
import { ScatterChart } from "../../ChartsComponent/ScatterChart/ScatterChart";
import type { GroupInput } from "../../ChartsComponent/ScatterChart/scatterUtils";
import { severityColorForLabel, verdictColorForLabel } from "./chartTheme";
import { buildSrSummary, SCATTER_PLOT_PX, type NormalizedChart } from "./resolveChart";

/** Mark radius in px. Small enough that neighbours stay visible on a crowded plot. */
const MARK_RADIUS = 6;

/** Verdicts first and worst-first, so the legend reads in the order a reader cares about. */
const GROUP_RANK: Record<string, number> = { failed: 0, fail: 0, passed: 1, pass: 1 };

/**
 * The groups to declare, coloured and ordered ahead of the data.
 *
 * <h3>Why declaring them matters</h3>
 * Left undeclared, the shared component assigns hues *positionally by first-seen group*. The drift
 * mapper sorts worst-first, so the first row's status takes hue #0 — meaning the same chart drawn
 * for two different SSPs can swap red and green depending on which control happens to be worst.
 * Declaring the groups pins both the colour and the legend order to the meaning, not the ordering.
 *
 * Ids are the raw row values, lowercase and untouched: the component matches them
 * case-sensitively, so a title-cased "Failed" would silently become a second, differently coloured
 * group rather than matching the "failed" the rows carry.
 */
function declaredGroups(chart: NormalizedChart): GroupInput[] | undefined {
  if (!chart.groupKey) return undefined;
  const key = chart.groupKey;

  const seen: string[] = [];
  for (const row of chart.rows) {
    const raw = row[key];
    const id = typeof raw === "string" || typeof raw === "number" ? String(raw).trim() : "";
    if (id && !seen.includes(id)) seen.push(id);
  }
  if (!seen.length) return undefined;

  // Stable sort, so anything that is not a verdict keeps its first-appearance order.
  const ordered = [...seen].sort((a, b) => rank(a) - rank(b));

  return ordered.map((id) => {
    // Verdict first, then severity — which also covers "unknown" and its spellings, mapping them
    // onto the same grey the rest of the panel uses for "no verdict".
    const color = verdictColorForLabel(id) ?? severityColorForLabel(id);
    const label = VERDICT_LABELS[id.toLowerCase()];
    return { id, ...(color ? { color } : {}), ...(label ? { label } : {}) };
  });
}

/**
 * Shortened verdicts, matching the scan timeline's legend.
 *
 * Left alone, the shared component humanises the raw row value into "Passed" / "Failed". Both drift
 * charts sit in the same conversation, so the two keys should not word the same thing differently.
 * Anything that is not a verdict still falls through to that default.
 */
const VERDICT_LABELS: Record<string, string> = {
  passed: "Pass",
  pass: "Pass",
  failed: "Fail",
  fail: "Fail",
};

function rank(id: string): number {
  return GROUP_RANK[id.toLowerCase()] ?? 2;
}


const CONTROL_ID_KEYS = new Set(["controlId", "control_id"]);

function withCasedLabels(chart: NormalizedChart): NormalizedChart {
  const key = chart.labelKey;
  if (!key || !CONTROL_ID_KEYS.has(key)) return chart;
  return {
    ...chart,
    rows: chart.rows.map((row) => {
      const value = row[key];
      return typeof value === "string" ? { ...row, [key]: value.toUpperCase() } : row;
    }),
  };
}

export function ChatScatter({ chart: input }: { chart: NormalizedChart }) {
  const chart = withCasedLabels(input);
  const groups = declaredGroups(chart);

  return (
    <ScatterChart
      // Uniform marks, never bubbles. Size is area-proportional over the value *range*, so a
      // narrow range — 49 to 196 scans, say — is stretched across the whole radius band and reads
      // as a far bigger difference than it is. A third measure is worth showing; it is not worth
      // showing as area. It stays in the tooltip and the table instead, via zKey below.
      variant="scatter"
      dotRadius={MARK_RADIUS}
      // Diamonds, matching the Drift Management screen, which already draws its
      // scan markers as diamonds — the chat chart summarises that same data, so
      // the two should not disagree about what a point looks like.
      markShape="diamond"
      data={chart.rows}
      xKey={chart.categoryKey}
      yKey={chart.yKey}
      zKey={chart.zKey}
      groupKey={chart.groupKey}
      labelKey={chart.labelKey}
      groups={groups}
      
      groupLabel="Status"
      xAxisLabel={chart.xLabel}
      yAxisLabel={chart.yLabel}
      height={SCATTER_PLOT_PX}
      // Every declared group must fit: a declared group consumes a slot even when it has no rows,
      // and anything past the cap is folded into a neutral "Other" — which on a status chart would
      // quietly merge two verdicts into one grey blob.
      maxGroups={groups?.length ?? 1}
      // Both axes are measures, and a scatter read against a floating baseline overstates how far
      // apart its points are.
      xStartsAtZero
      yStartsAtZero
      // ChartMessage's card owns the header, the state cards and the surrounding chrome, so the
      // component must not draw a second set inside it.
      showDataTable={false}
      showWarnings={false}
      // ChartSpec carries one global unit, and the shared component applies `unit` to y — the same
      // meaning. An x unit belongs in xAxisLabel ("Failure rate %"), not here.
      unit={chart.unit}
      ariaLabel={buildSrSummary(chart)}
    />
  );
}
